// The `default` structure symbol set: card 04 of mockup round 054, "THE
// MARK OF TODAY, KEYED" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// The mark of today stays: the core disc r7, the ring r12 and the rotating
// dashed halo r22. Each kind takes a glyph knocked out of the core in the
// water's dark, and a halo dash count of its own. The Throne draws a little
// larger, because it is the capital.
//
// **What this file draws, and what the layer draws.** The atlas bakes the
// STILL mark, so the contested tell reads at motion off and in greyscale
// (R1104). The rotating halo is the one live part, and the site layer draws
// it as one circle instance for THIS set alone; `DEFAULT_HALOS` below states
// what it draws for each kind. Every other set draws no live halo.
//
// The contested tell is the notched ring with the attacker's tick in the
// notch, plus a second halo at r18 in the attacker's ink. Both are still.
//
// Every offset, weight, radius and alpha below is the figure card 04 draws.
// The Reef Breakwater (R2085) is no kind of card 04: it takes the card's
// ring and core, and its own glyph, the shield.

import type { MarkGraphics } from '../marks';
import {
  arcPath, dashedRing, disc, line, quadratic, ring, rot, stroke, TILT,
  WATER, setOf, type MarkKind, type StructureDraw, type StructureInk, type StructureSet,
} from './shared';

/** The core and the ring of one kind. The Throne carries the larger pair. */
const CORE_R = 7;
const THRONE_CORE_R = 8;
const RING_R = 12;
/** The second halo of a contested object, in the attacker's ink. */
const FOE_HALO_R = 18;
const FOE_HALO_FILL = 0.55;
/** The angle the second halo stands at. Card 04 turns it against the first. */
const FOE_HALO_START = 0.3;
/** The notch in the ring: this much of a radian is cut at the top. */
const NOTCH = 0.5;

/**
 * What the site layer draws as the live halo of one kind (R1104).
 *
 * `dashes` of 0 is a whole ring, which is what the Archive Vault wears.
 * `fill` is the lit fraction of one dash cell, the figure the circle batch
 * takes. The alpha is the layer's, because it follows the holder.
 */
export interface DefaultHalo {
  r: number;
  dashes: number;
  width: number;
  fill: number;
}

/** The lit fraction of one dash cell — card 04 draws `[L*0.55, L*0.45]`. */
const HALO_FILL = 0.55;

/**
 * The halo of every kind: the dash count of card 04, the Throne's wider
 * radius and the Coral Ward's heavier line.
 */
export const DEFAULT_HALOS: Record<MarkKind, DefaultHalo> = {
  spire: { r: 22, dashes: 3, width: 1.2, fill: HALO_FILL },
  forge: { r: 22, dashes: 12, width: 1.2, fill: HALO_FILL },
  kelp: { r: 22, dashes: 6, width: 1.2, fill: HALO_FILL },
  battery: { r: 22, dashes: 4, width: 1.2, fill: HALO_FILL },
  throne: { r: 24, dashes: 8, width: 1.2, fill: HALO_FILL },
  drydock: { r: 22, dashes: 2, width: 1.2, fill: HALO_FILL },
  archive: { r: 22, dashes: 0, width: 1.2, fill: HALO_FILL },
  marshal: { r: 22, dashes: 16, width: 1.2, fill: HALO_FILL },
  ward: { r: 22, dashes: 5, width: 2, fill: HALO_FILL },
  // The Reef Breakwater (R2085): ten short dashes, apart from the five
  // heavy ones of the Ward and the four of the Battery.
  breakwater: { r: 22, dashes: 10, width: 1.2, fill: HALO_FILL },
};

/**
 * The glyph of one kind, knocked out of the core in the water's dark.
 *
 * It is `glyph()` of card 04. The ink is always the water, because the core
 * under it carries the holder's colour.
 */
function glyph(g: MarkGraphics, kind: MarkKind): void {
  const ink = WATER;
  switch (kind) {
    case 'spire': {
      // The mast, with one radio arc over its head.
      line(g, 0, 4, 0, -2, ink, 1, 1.4);
      arcPath(g, 0, -2, 3, -2.5, -0.65);
      stroke(g, ink, 1, 1.1);
      break;
    }
    case 'forge': {
      // The chimney on its hearth, with the smoke out of the mouth.
      g.poly([-3, 4, -2, -1, 2, -1, 3, 4], false);
      stroke(g, ink, 1, 1.2);
      line(g, 0, -2, 0, -4.5, ink, 1, 1.2);
      break;
    }
    case 'kelp': {
      // Three fronds, each one quadratic curve of the card.
      for (const x of [-3, 0, 3]) {
        const pts = [x, 4.5];
        quadratic(pts, x, 4.5, x * 2.2, 0, x * 1.4, -4.5);
        g.poly(pts, false);
        stroke(g, ink, 1, 1.2);
      }
      break;
    }
    case 'battery': {
      // The heavy tube, aimed up and to the right. The card rotates the
      // context by TILT; there is no transform here, so the two ends carry
      // the turn.
      const [x0, y0] = rot(-4, 0, TILT);
      const [x1, y1] = rot(4, 0, TILT);
      line(g, x0, y0, x1, y1, ink, 1, 2.6);
      break;
    }
    case 'throne': {
      // The crown, filled.
      g.poly([-4.5, 3.5, -4.5, -3, -2, -0.5, 0, -4.5, 2, -0.5, 4.5, -3, 4.5, 3.5]);
      g.fill({ color: ink, alpha: 1 });
      break;
    }
    case 'drydock': {
      // The cradle: one quadratic curve, closed across its mouth.
      const pts = [-4.5, -1];
      quadratic(pts, -4.5, -1, 0, 5, 4.5, -1);
      g.poly(pts);
      stroke(g, ink, 1, 1.2);
      break;
    }
    case 'archive': {
      // The keyhole: a disc over a tapered slot.
      disc(g, 0, -1.5, 1.8, ink, 1);
      g.poly([-1, 0, -2, 4.5, 2, 4.5, 1, 0]);
      g.fill({ color: ink, alpha: 1 });
      break;
    }
    case 'marshal': {
      // Three columns under one architrave.
      for (const x of [-3, 0, 3]) line(g, x, 4, x, -2, ink, 1, 1.2);
      line(g, -4, -3.5, 4, -3.5, ink, 1, 1.2);
      break;
    }
    case 'ward': {
      // The cross of the mending aura.
      line(g, -4, 0, 4, 0, ink, 1, 1.6);
      line(g, 0, -4, 0, 4, ink, 1, 1.6);
      break;
    }
    case 'breakwater': {
      // The shield that stops the rounds: a flat head, two straight sides
      // and two curves to the point at the foot.
      const pts = [-3.8, -4, 3.8, -4, 3.8, 0.2];
      quadratic(pts, 3.8, 0.2, 3.6, 3.2, 0, 4.8);
      quadratic(pts, 0, 4.8, -3.6, 3.2, -3.8, 0.2);
      g.poly(pts);
      stroke(g, ink, 1, 1.3);
      break;
    }
  }
}

/**
 * One kind of the `default` set, still.
 *
 * The order is the card's: the contested tell first, then the ring or the
 * notched ring, then the core, then the glyph. The live halo is the layer's
 * and is not drawn here.
 */
function markOf(kind: MarkKind): StructureDraw {
  const thr = kind === 'throne';
  const halo = DEFAULT_HALOS[kind];
  const ringWidth = thr ? 2 : 1.2;
  const coreR = thr ? THRONE_CORE_R : CORE_R;
  return (g: MarkGraphics, ink: StructureInk): void => {
    const a = ink.held ? 0.95 : 0.6;
    if (ink.foe !== 0) {
      // The second halo, in the attacker's ink. The card turns it against
      // the first; the atlas bakes it still, so it stands at one angle.
      dashedRing(g, FOE_HALO_R, halo.dashes || 8, FOE_HALO_FILL,
        ink.foe, 0.9, 1.6, FOE_HALO_START);
      // The ring, notched at the top, with the attacker's tick in the notch.
      arcPath(g, 0, 0, RING_R, -Math.PI / 2 + NOTCH, Math.PI * 1.5 - NOTCH);
      g.stroke({ width: ringWidth, color: ink.own, alpha: a });
      line(g, 0, -15, 0, -9, ink.foe, 0.95, 2);
    } else if (kind === 'kelp') {
      // The Kelp Terrace's ring is dashed even while nobody pledges.
      dashedRing(g, RING_R, 12, 0.25, ink.own, a, ringWidth);
    } else {
      ring(g, 0, 0, RING_R, ink.own, a, ringWidth);
    }
    disc(g, 0, 0, coreR, ink.own, a);
    glyph(g, kind);
  };
}

/** Card 04, all nine kinds. */
export const DEFAULT_SET: StructureSet = setOf(markOf);

/**
 * The Reef Breakwater in the form of card 04 (R2085): every set draws this
 * one mark, because no set of round 054 draws an arcade structure.
 */
export const DEFAULT_BREAKWATER: StructureDraw = markOf('breakwater');
